"use client"

import { useState, useEffect, useCallback } from "react"
import { Star, Plus, Loader2 } from "lucide-react"
import ReviewCard, { } from "./ReviewCard"
import ReviewModal, { type ReviewData } from "./ReviewModal"

interface Props {
  subjectId: string
  subjectName: string
  currentUserId?: string
}

interface ReviewsResponse {
  reviews: ReviewData[]
  total: number
  verifiedCount: number
  avgRating: number
}

function StarDisplay({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className="w-5 h-5"
          fill={rating >= s ? "#F59E0B" : rating >= s - 0.5 ? "#F59E0B" : "none"}
          stroke={rating >= s ? "#F59E0B" : "#D1D5DB"}
        />
      ))}
    </div>
  )
}

export default function ReviewsSection({ subjectId, subjectName, currentUserId }: Props) {
  const [data, setData] = useState<ReviewsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const res = await fetch(`/api/reviews/user/${subjectId}`)
    if (res.ok) setData(await res.json())
    setLoading(false)
  }, [subjectId])

  useEffect(() => { load() }, [load])

  // Detect Stripe success redirect
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)
      if (params.get("review_verified") === "1") {
        load() // refresh so the verified badge shows immediately
        // Clean up the URL param without a reload
        const url = new URL(window.location.href)
        url.searchParams.delete("review_verified")
        window.history.replaceState({}, "", url.toString())
      }
    }
  }, [load])

  const myReview = data?.reviews.find((r) => r.author.id === currentUserId)
  const canLeaveReview = !!currentUserId && currentUserId !== subjectId && !myReview

  function handleSaved(review: ReviewData) {
    setData((prev) => {
      if (!prev) return prev
      const exists = prev.reviews.find((r) => r.id === review.id)
      const reviews = exists
        ? prev.reviews.map((r) => (r.id === review.id ? review : r))
        : [review, ...prev.reviews]
      const total = exists ? prev.total : prev.total + 1
      const avg =
        Math.round((reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) * 10) / 10
      return { ...prev, reviews, total, avgRating: avg }
    })
  }

  function handleDeleted(id: string) {
    setData((prev) => {
      if (!prev) return prev
      const reviews = prev.reviews.filter((r) => r.id !== id)
      const total = reviews.length
      const avg = total > 0
        ? Math.round((reviews.reduce((s, r) => s + r.rating, 0) / total) * 10) / 10
        : 0
      const verifiedCount = reviews.filter((r) => r.verified).length
      return { ...prev, reviews, total, verifiedCount, avgRating: avg }
    })
  }

  function handleUpdated(review: ReviewData) {
    setData((prev) => {
      if (!prev) return prev
      const reviews = prev.reviews.map((r) => (r.id === review.id ? review : r))
      const avg =
        Math.round((reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) * 10) / 10
      return { ...prev, reviews, avgRating: avg }
    })
  }

  return (
    <div className="mt-6 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Reviews</h2>
          {data && data.total > 0 && (
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <StarDisplay rating={data.avgRating} />
              <span className="text-sm font-semibold text-gray-800">{data.avgRating}</span>
              <span className="text-sm text-gray-500">
                ({data.total} review{data.total !== 1 ? "s" : ""}
                {data.verifiedCount > 0 && `, ${data.verifiedCount} verified`})
              </span>
            </div>
          )}
        </div>

        {canLeaveReview && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg"
          >
            <Plus className="w-4 h-4" />
            Leave a Review
          </button>
        )}

        {myReview && (
          <span className="text-xs text-gray-500 italic">You&apos;ve already reviewed this user</span>
        )}
      </div>

      {/* Body */}
      <div className="px-5 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : !data || data.total === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">
            No reviews yet.
            {canLeaveReview && (
              <>
                {" "}
                <button
                  onClick={() => setShowModal(true)}
                  className="text-blue-600 hover:underline"
                >
                  Be the first to leave one.
                </button>
              </>
            )}
          </p>
        ) : (
          <div className="space-y-3">
            {data.reviews.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                currentUserId={currentUserId}
                subjectName={subjectName}
                onUpdated={handleUpdated}
                onDeleted={handleDeleted}
              />
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <ReviewModal
          subjectId={subjectId}
          subjectName={subjectName}
          existingReview={null}
          onClose={() => setShowModal(false)}
          onSaved={(r) => { handleSaved(r); setShowModal(false) }}
        />
      )}
    </div>
  )
}
